import { default as FNS, logIn } from '../api';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { addKnowns } from './knownDataSlice';

// Dashboard brand filter ('all' | 'abuss' | 'triobuss'), remembered across reloads
export const BRAND_FILTER_ALL = 'all';
const BRAND_FILTER_STORAGE_KEY = 'dashboardBrandFilter';

function loadBrandFilter() {
  try {
    return window.localStorage.getItem(BRAND_FILTER_STORAGE_KEY) || BRAND_FILTER_ALL;
  } catch (e) {
    return BRAND_FILTER_ALL;
  }
}

function storeBrandFilter(value) {
  try {
    window.localStorage.setItem(BRAND_FILTER_STORAGE_KEY, value);
  } catch (e) {
    // Storage unavailable (private mode etc.) - the filter just won't persist
  }
}

// For simulating slow connection
function wait(time) {
  return new Promise(resolve => setTimeout(resolve, time));
}





export const TYPES_META = {
  queries: {
    id_field: "query_id",
  },
  acceptedQueries: {
    id_field: "query_response_id",
  },
  jobs: {
    id_field: "job_id",
  },
  arrangements: {
    id_field: "arrangement_id",
  },
  inactiveArrangements: {
    id_field: "arrangement_id",
  },
  rejected: {
    id_field: "query_id",
  },
};

const DATA_TYPES = {
  newQueries: {
    fn: FNS.getNewQueries,
    type: "queries",
  },
  answeredQueries: {
    fn: FNS.getAnsweredQueries,
    type: "acceptedQueries",
  },
  confirmedQueries: {
    fn: FNS.getConfirmedQueries,
    type: "jobs",
  },
  upcomingJobs: {
    fn: FNS.getUpcomingJobs,
    type: "jobs",
  },
  unreadFeedback: {
    fn: FNS.getUnreadFeedback,
    type: "jobs",
  },
  arrangements: {
    fn: FNS.getArrangements,
    type: "arrangements",
  },
  exceptions: {
    fn: FNS.getArrangementExceptions,
    type: "exceptions",
  },
  inactiveArrangements: {
    fn: FNS.getInactiveArrangements,
    type: "inactiveArrangements",
  },
  myUpcomingJobs: {
    fn: FNS.getMyUpcomingJobs,
    type: "jobs",
  },
  myFinishedJobs: {
    fn: FNS.getMyFinishedJobs,
    type: "jobs",
  },
  myFeedbacklessJobs: {
    fn: FNS.getMyFeedbacklessJobs,
    type: "jobs",
  },
  feedbacklessJobs: {
    fn: FNS.getFeedbacklessJobs,
    type: "jobs",
  },
  myArrangements: {
    fn: FNS.getMyArrangements,
    type: "arrangements",
  },
  myExceptions: {
    fn: FNS.getMyExceptions,
    type: "exceptions",
  },
  // NOTE: `archive` and `rejected` are intentionally NOT here. They use
  // server-side pagination/search/sort and have dedicated thunks
  // (fetchArchive / fetchRejected) so they are excluded from fetchAll().
};

// Default paging/search/sort params for the paginated archive & rejected tables.
export const ARCHIVE_PAGE_SIZE = 10;
const DEFAULT_ARCHIVE_PARAMS = { limit: ARCHIVE_PAGE_SIZE, offset: 0, search: '', sortKey: '', sortDir: '', all: false, brand: BRAND_FILTER_ALL };

export const fetch = Object.entries(DATA_TYPES).reduce((res, [name, { fn, type }]) => {

  res[name] = createAsyncThunk('shownData/fetch/' + name, async (payload, { dispatch }) => {
    
    const res = await fn();
    dispatch(addKnowns({ values: res, type }));
    dispatch(shownDataSlice.actions.replaceData({ location: name, values: res }));
  });

  return res;

}, {});

export const fetchAll = createAsyncThunk('shownData/fetchAll', async (payload, { dispatch }) => {
  try {
    let promises = [];

    for(const name of Object.keys(DATA_TYPES)) {
      promises.push(dispatch(fetch[name]()));
    }

    await Promise.all(promises);
  } catch(err) {
    console.log(err);
  }
});

export const fetchArchive = createAsyncThunk('shownData/fetchArchive', async (payload, { dispatch }) => {
  const { limit, offset, search, sortKey, sortDir, all, brand } = { ...DEFAULT_ARCHIVE_PARAMS, ...payload };

  const { archive: res, total } = await FNS.getArchive(limit, offset, search, sortKey, sortDir, all, brand);

  const jobs = res.filter(item => item["type"] == "job");
  const queries = res.filter(item => item["type"] == "query");
  const confirmedQueries = res.filter(item => item["type"] == "confirmedQuery");
  const answeredQueries = res.filter(item => item["type"] == "answeredQuery");

  dispatch(addKnowns({ values: jobs, type: "jobs" }));
  dispatch(addKnowns({ values: queries, type: "queries" }));
  dispatch(addKnowns({ values: confirmedQueries, type: "jobs" }));
  dispatch(addKnowns({ values: answeredQueries, type: "acceptedQueries" }));

  dispatch(shownDataSlice.actions.replaceData({ location: "archive", values: res }));
  dispatch(shownDataSlice.actions.setTotal({ location: "archiveTotal", value: total }));
});

export const fetchRejected = createAsyncThunk('shownData/fetchRejected', async (payload, { dispatch }) => {
  const { limit, offset, search, sortKey, sortDir, all, brand } = { ...DEFAULT_ARCHIVE_PARAMS, ...payload };

  const { rejected: res, total } = await FNS.getRejected(limit, offset, search, sortKey, sortDir, all, brand);

  dispatch(addKnowns({ values: res, type: "rejected" }));
  dispatch(shownDataSlice.actions.replaceData({ location: "rejected", values: res }));
  dispatch(shownDataSlice.actions.setTotal({ location: "rejectedTotal", value: total }));
});


const shownDataSlice = createSlice({
  name: 'shownData',
  initialState: {
    newQueries: [],
    answeredQueries: [],
    confirmedQueries: [],
    upcomingJobs: [],
    unreadFeedback: [],
    arrangements: [],
    myUpcomingJobs: [],
    myFinishedJobs: [],
    myFeedbacklessJobs: [],
    feedbacklessJobs: [],
    myArrangements: [],
    archive: [],
    rejected: [],
    archiveTotal: 0,
    rejectedTotal: 0,
    brandFilter: loadBrandFilter(),
  },
  reducers: {
    setBrandFilter: (state, { payload }) => {
      state.brandFilter = payload;
      storeBrandFilter(payload);
    },
    replaceData: (state, { payload: { location, values } }) => {
      state[location] = values;
    },
    setTotal: (state, { payload: { location, value } }) => {
      state[location] = value;
    },
    updateData: (state, { payload: { values, type } }) => {
      for(let newItem of values) {
        for(const category of state) {
          for(let i = 0; i < category.length; i++) {
            // Check if their corresponding id field matches
            // Fetches id field from TYPES_META
            if(category[i][TYPES_META[type].id_field] == newItem[TYPES_META[type].id_field]) {
              category[i] = newItem;
            }
          }
        }
      }
    }
  },
});

export const { setBrandFilter } = shownDataSlice.actions;

export default shownDataSlice.reducer;
